/**
 * GOING STALE, IN FOUR TIERS — how long since they said anything, and how much of
 * the card should still be there.
 *
 * The third card state, beside client/src/crm/waiting.mjs (the ball is in our court)
 * and client/src/crm/hold.mjs (we put the ball down). This one is about THEIR
 * silence, and the card fades with it: warm, cooling, cold, gone. Everything
 * dissolves as it cools except the number of days, which stays inked and gets
 * heavier — a board cannot tell 15 days from 21 by shade alone, so the shade says
 * roughly how dead and the number says exactly.
 *
 * ONE NUMBER, AND IT IS THE GHOST RULE'S OWN. `chip.ghost.quiet_days` counts from
 * the last human sign of life FROM THEM — a reply, a call, a meeting, a form — and
 * from the record's creation when we have never heard from them at all. It is
 * computed server-side by the same predicate the weekly sweep runs, so the card and
 * the job that proposes writing a company off measure one silence.
 *
 * IT IS NOT THE CLOCK BEHIND THE `stale` TAG, which this fade replaces. That one
 * measures the last activity of ANY kind, including our own outbound, so it resets
 * every time we write: Digital Science has been silent 49 days and carries no stale
 * tag, because we emailed them last week. Reading that card as fresh is the same
 * error as counting our own follow-up as a chase.
 *
 * THE BANDS COME FROM SETTINGS, never from a literal here. `stale_days` is already
 * the line where this system says a company has gone quiet and `ghost_days` is
 * already the line where it proposes writing them off, so the fade rides on the two
 * numbers that are already tuned, and a card reads "gone" exactly when the sweep
 * would act. Moving a tier is a settings change. With no thresholds in hand — the
 * chips request failed, or has not landed — this returns null and every card renders
 * exactly as it did before.
 */

const LABELS = { cooling: "no reply", cold: "ghosting", gone: "ghosted" };

/**
 * The tier a card is in, or null for a card that should not fade at all: one still
 * inside the stale threshold, or one whose silence we cannot date.
 *
 * WARM IS NULL ON PURPOSE. Below the threshold the silence is not news — of course a
 * deal we spoke to on Tuesday is quiet — and the mockup's warm card is today's card:
 * solid border, its shadow, "8d in stage" in the corner. The big number takes that
 * corner over the moment the silence crosses the line this system already calls
 * stale, and from there it never leaves.
 */
export function silenceTier(chip, thresholds) {
  const g = chip && chip.ghost;
  const days = g && g.quiet_days;
  if (!Number.isFinite(days)) return null;

  const stale = thresholds && thresholds.stale_days;
  const ghost = thresholds && thresholds.ghost_days;
  if (!Number.isFinite(stale) || !Number.isFinite(ghost)) return null;
  if (days < stale) return null;

  // Cooling runs to twice the stale threshold — the same knob, so one setting still
  // governs where the fade starts and how fast it deepens.
  const tier = days < stale * 2 ? "cooling" : days < ghost ? "cold" : "gone";
  return {
    tier,
    days,
    label: LABELS[tier],
    // Whether the weekly sweep can ever ask this question itself. It cannot until
    // the company has been chased, which is true of most of the board's dead deals,
    // and is why the last tier asks rather than waiting to be told.
    ghosted: !!g.ghosted,
    outbound_short_by: Number(g.outbound_short_by) || 0,
  };
}

/** What the number means, spelled out on the hover: the card only has room for "days silent". */
export function silenceTitle(silence) {
  if (!silence) return undefined;
  const parts = [`${silence.days} days since they last replied, called or met`];
  if (silence.tier === "gone") {
    parts.push(
      silence.ghosted
        ? "the weekly sweep proposes closing this"
        : silence.outbound_short_by > 0
          ? "the sweep will not propose closing it — nobody has chased them"
          : null
    );
  }
  return parts.filter(Boolean).join(" — ");
}
